import {
  ChannelMappingsResponse,
  ChannelResponse,
  ChannelSource,
  ChannelValue,
  PosProvider,
  RevenueSourcesResponse,
} from 'api/captureRate'

export type RevenueSourceRow = {
  id: string
  provider: PosProvider
  providerKey: string
  providerLabel: string | null
  value: ChannelValue | null
  source: ChannelSource | null
  noChannel: boolean
  needsReview: boolean
  totalCents: number
  orderCount: number
  percentOfRevenue: number
}

type AggregatedChannel = {
  provider: PosProvider
  providerKey: string
  providerLabel: string | null
  value: ChannelValue | null
  source: ChannelSource | null
  noChannel: boolean
  needsReview: boolean
  totalCents: number
  orderCount: number
}

export function isConfirmable(
  row: Pick<RevenueSourceRow, 'needsReview' | 'value'>
): boolean {
  return row.needsReview && row.value != null
}

function aggregationKey(channel: ChannelResponse, noChannel: boolean): string {
  return `${channel.provider}::${channel.provider_key}::${noChannel}`
}

// needs_review comes straight from the API (ChannelMappingEntity#needs_review)
// -- noChannel is layered on top since that's a revenue-source-level concept
// the channel entity itself doesn't know about.
function needsReview(mappingNeedsReview: boolean, noChannel: boolean): boolean {
  return !noChannel && mappingNeedsReview
}

// Sums revenue_sources rows into one row per (provider, provider_key), across
// whatever locations the response covers.
export function aggregateByProviderAndKey(
  sources: RevenueSourcesResponse['sources']
): AggregatedChannel[] {
  const byKey = new Map<string, AggregatedChannel>()

  sources.forEach(source => {
    const { channel } = source
    if (channel.disabled) return

    const mapKey = aggregationKey(channel, source.no_channel)
    const existing = byKey.get(mapKey)

    if (existing) {
      existing.totalCents += source.total_cents
      existing.orderCount += source.order_count
      return
    }

    byKey.set(mapKey, {
      provider: channel.provider,
      providerKey: channel.provider_key,
      providerLabel: channel.provider_label,
      value: channel.value,
      source: channel.source,
      noChannel: source.no_channel,
      needsReview: needsReview(channel.needs_review, source.no_channel),
      totalCents: source.total_cents,
      orderCount: source.order_count,
    })
  })

  return Array.from(byKey.values())
}

// Adds a $0/0-transaction row for every mapped (provider, provider_key)
// missing from `observed`, so a mapped channel with no revenue in the window
// still shows.
export function fillZeroRevenueSources(
  observed: AggregatedChannel[],
  mappings: ChannelMappingsResponse['mappings']
): AggregatedChannel[] {
  const observedKeys = new Set(
    observed.map(source => `${source.provider}::${source.providerKey}`)
  )

  const zeroFilled: AggregatedChannel[] = mappings
    .filter(
      mapping =>
        !mapping.disabled &&
        !observedKeys.has(`${mapping.provider}::${mapping.provider_key}`)
    )
    .map(mapping => ({
      provider: mapping.provider,
      providerKey: mapping.provider_key,
      providerLabel: mapping.provider_label,
      value: mapping.value,
      source: mapping.source,
      noChannel: false,
      needsReview: needsReview(mapping.needs_review, false),
      totalCents: 0,
      orderCount: 0,
    }))

  return [...observed, ...zeroFilled]
}

function toRow(
  channel: AggregatedChannel,
  totalRevenueCents: number
): RevenueSourceRow {
  return {
    id: channel.noChannel
      ? `${channel.provider}::__no_channel__`
      : `${channel.provider}::${channel.providerKey}`,
    provider: channel.provider,
    providerKey: channel.providerKey,
    providerLabel: channel.providerLabel,
    value: channel.value,
    source: channel.source,
    noChannel: channel.noChannel,
    needsReview: channel.needsReview,
    totalCents: channel.totalCents,
    orderCount: channel.orderCount,
    percentOfRevenue:
      totalRevenueCents > 0 ? channel.totalCents / totalRevenueCents : 0,
  }
}

// Needs-review rows first, then mapped, then noChannel rows last, each as
// its own section.
function sortWeight(row: RevenueSourceRow): number {
  if (row.needsReview) return 0
  if (row.noChannel) return 2
  return 1
}

// Within a section, largest revenue share first. Array.prototype.sort
// is stable per spec, so an exact tie (e.g. two $0 rows) keeps source order.
export function sortRows(rows: RevenueSourceRow[]): RevenueSourceRow[] {
  return [...rows].sort((a, b) => {
    const sectionDiff = sortWeight(a) - sortWeight(b)
    return sectionDiff !== 0
      ? sectionDiff
      : b.percentOfRevenue - a.percentOfRevenue
  })
}

export function buildRevenueSourceRows(
  channelMappings: ChannelMappingsResponse['mappings'],
  revenueSources: RevenueSourcesResponse['sources']
): RevenueSourceRow[] {
  const observed = aggregateByProviderAndKey(revenueSources)
  const withZeroFill = fillZeroRevenueSources(observed, channelMappings)
  const totalRevenueCents = withZeroFill.reduce(
    (sum, channel) => sum + channel.totalCents,
    0
  )

  return sortRows(
    withZeroFill.map(channel => toRow(channel, totalRevenueCents))
  )
}
